import React, { useState, useEffect } from "react";
import {
  DoubleArrow,
  Edit,
  Grading,
  Diversity3,
  Category,
} from "@mui/icons-material";
import { themes, defaultAllSelection } from "services/constants";
import {
  CardContent,
  Box,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableCell,
  TableHead,
  Drawer,
  Tooltip,
  Paper,
  Alert,
  Card,
  CardHeader,
  IconButton,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { KeyboardArrowUp, KeyboardArrowRight } from "@mui/icons-material";
import { customGetAPI } from "services/utils/CustomAPI";
import { EditAssignmentPanel } from "./EditAssignmentPanel";
import { tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";

export const ViewAssignmentPanel = ({
  onClose,
  reCallApi,
  userData,
  setUpdateData,
  screenAccess,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { setValue, watch } = useForm({
    mode: "onBlur",
    defaultValues: {
      assignment_data: [],
    },
  });
  const { domain } = useSelector((state) => state.tokenReducer);
  const [expanded, setExpanded] = useState("");
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [tiggerApi, setTriggerApi] = useState(true);
  const { result: gradeHistory, setLoad: reCallGradeApi } = useFetch(
    `${domain}administration/shiftroster/userassignment/shiftgrade/shift-grade-details/?department_id=${defaultAllSelection}&shift_grade_id=${defaultAllSelection}&user_id=${userData?.user_id}&company_id=${requestCompanyId}`
  );
  const { result: groupHistoy, setLoad: reCallGroupApi } = useFetch(
    `${domain}administration/shiftroster/userassignment/shiftgroup/shift-group-details/?department_id=${defaultAllSelection}&shift_group_id=${defaultAllSelection}&user_id=${userData?.user_id}&company_id=${requestCompanyId}`
  );

  const { result: categoryHistory, setLoad: reCallCategoryApi } = useFetch(
    `${domain}shift-category-details/?department_id=${defaultAllSelection}&shift_category_id=${defaultAllSelection}&user_id=${userData?.user_id}`
  );

  const handleChange = (index) => (event, isExpanded) => {
    setExpanded(isExpanded ? index : false);
  };

  const handleEditShift = () => {
    setIsEditPanelOpen(true);
  };

  const handleEditClosePanel = () => {
    setIsEditPanelOpen(false);
  };

  useEffect(() => {
    const getGradeData = async () => {
      const res = await customGetAPI(
        `${domain}administration/shiftroster/userassignment/shiftgrade/users/assignments/?user_id=${userData?.user_id}&company_id=${requestCompanyId}`
      );
      if (res.ok) {
        setValue("assignment_data", res?.responseDetails);
        setTriggerApi(false);
      } else {
        setTriggerApi(false);
      }
    };
    if (tiggerApi && userData?.user_id) {
      getGradeData();
      setTriggerApi(false);
    }
  }, [domain, setValue, tiggerApi, userData.id, userData?.user_id, watch, requestCompanyId]);

  const assignmentItems = [
    {
      category: "Shift Grade",
      value:
        watch("assignment_data")?.length !== 0
          ? watch("assignment_data")?.shift_grades[0]?.name
          : "",
      icon: <Grading color="success" />,
      details: gradeHistory?.data ?? [],
      effective_from:
        watch("assignment_data")?.length !== 0
          ? watch("assignment_data")?.shift_grades[0]?.effective_from
          : "",
    },
    {
      category: "Shift Group",
      value:
        watch("assignment_data")?.length !== 0
          ? watch("assignment_data")?.shift_groups[0]?.name
          : "",
      icon: <Diversity3 color="success" />,
      details: groupHistoy?.data ?? [],
      effective_from:
        watch("assignment_data")?.length !== 0
          ? watch("assignment_data")?.shift_groups[0]?.effective_from
          : "",
    },
    {
      category: "Shift Category",
      value:
        watch("assignment_data")?.length !== 0
          ? watch("assignment_data")?.shift_categories[0]?.name
          : "",
      icon: <Category color="success" />,
      details: categoryHistory?.data ?? [],
      effective_from:
        watch("assignment_data")?.length !== 0
          ? watch("assignment_data")?.shift_categories[0]?.effective_from
          : "",
    },
  ];

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderBottom: `1px solid ${themes?.miliedLightGray}`,
            borderRadius: 0,
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
              }}
            >
              <Box sx={{ ml: 1 }}>
                <IconButton
                  onClick={onClose}
                  sx={{
                    color: themes?.grayColor,
                    "&:hover": {
                      backgroundColor: themes?.whiteColor,
                    },
                  }}
                >
                  <DoubleArrow />
                </IconButton>
              </Box>
              <Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
                <IconButton
                  onClick={() => {
                    if (screenAccess) {
                      handleEditShift();
                    } else {
                      showToast(
                        "error",
                        `Access denied: You are not authorized to modify assignment details`
                      );
                    }
                  }}
                  sx={{
                    color: themes?.greenColor,
                    backgroundColor: themes?.primary,
                    borderRadius: "50%",
                    "&:hover": { backgroundColor: themes?.primary },
                    mr: 2,
                    mt: 0.5,
                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ ml: 2.2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Assignment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and edit user shift assignment
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            <Box>
              <UserDetailsRow
                userDetails={{
                  image_url: userData?.image_url,
                  employee_id: userData?.employee_id,
                  user_name: userData?.employee_name,
                  department_name: userData?.department,
                  designation: userData?.designation,
                }}
              />
            </Box>

            <Box>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  border: `1px solid ${themes?.miliedLightGray}`,
                }}
              >
                <CardHeader
                  title="Current Assignment"
                  titleTypographyProps={{
                    variant: "h6",
                    fontWeight: 700,
                    color: themes?.mutedpurple,
                    fontSize: 18,
                  }}
                />
                <CardContent>
                  {assignmentItems.map((item, index) => (
                    <Accordion
                      key={index}
                      expanded={expanded === index}
                      onChange={handleChange(index)}
                      sx={{
                        "&:before": { display: "none" },
                        boxShadow: "none",
                        borderBottom:
                          index !== assignmentItems.length - 1
                            ? `1px solid ${themes?.miliedLightGray}`
                            : "none",
                        borderRadius: 0,
                        m: 0,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          expanded === index ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowRight />
                          )
                        }
                        sx={{ flexDirection: "row-reverse" }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="85%"
                        >
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box
                              display="flex"
                              flexDirection="column"
                              sx={{ ml: 0.4 }}
                            >
                              {item.icon}
                            </Box>

                            <Box display="flex" flexDirection="column">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.category}
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {item.value}
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body2" color="text.secondary">
                              Effective from
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {item.effective_from}
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer>
                          <Table size="small" className="border">
                            <TableHead
                              sx={{
                                backgroundColor: themes.primary,
                                position: "sticky",
                                top: -1,
                                zIndex: 10,
                              }}
                            >
                              <TableRow>
                                <TableCell>
                                  {expanded === 0
                                    ? "Shift Grade"
                                    : expanded === 1
                                    ? "Shift Group"
                                    : expanded === 2
                                    ? "Shift Category"
                                    : ""}
                                </TableCell>
                                <TableCell>Assigned From</TableCell>
                                <TableCell>Assigned To</TableCell>
                              </TableRow>
                            </TableHead>
                            {item.details?.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8} align="center">
                                  <Alert severity="info">
                                    No data available
                                  </Alert>
                                </TableCell>
                              </TableRow>
                            ) : (
                              <TableBody>
                                {item.details.map((detail, i) => (
                                  <TableRow key={i}>
                                    <TableCell>
                                      {expanded === 0
                                        ? detail?.shift_grade_name
                                        : expanded === 1
                                        ? detail?.shift_group_name
                                        : expanded === 2
                                        ? detail?.shift_category_name
                                        : ""}
                                    </TableCell>
                                    <TableCell>
                                      {detail?.effective_from ?? "N/A"}
                                    </TableCell>
                                    <TableCell>
                                      {detail?.effective_to ?? "N/A"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            )}
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>
      <Drawer
        anchor="right"
        open={isEditPanelOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleEditClosePanel();
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.whiteColor,
          },
        }}
      >
        <EditAssignmentPanel
          onClose={handleEditClosePanel}
          userData={userData}
          reCallApi={reCallApi}
          AssignmentData={watch("assignment_data")}
          setTriggerApi={setTriggerApi}
          setUpdateData={setUpdateData}
          reCallGradeApi={reCallGradeApi}
          reCallGroupApi={reCallGroupApi}
          reCallCategoryApi={reCallCategoryApi}
        />
      </Drawer>
    </React.Fragment>
  );
};
